<template>
    <div>
        <GlobXpanel :title="$t('administration.api_key.api_keys')">
            <button @click="openCreatePanel" class="button button--margin button--small">
                <i class="fas fa-plus"></i> {{ $t('administration.api_key.create_api_key') }}
            </button>
            <InputText 
                type="text" 
                v-model="searchString" 
                :placeholder="$t('administration.api_key.search_placeholder')" 
                :style="'margin-left: 1rem; padding: .4rem 1rem'"
            />
            <GlobTable
                :maxHeight="50"
                :data="tableData"
                :clickRow="openDetailPanel"
                :actions="actions"
                :itemRenderFunctions="renderFunctions"
            />
        </GlobXpanel>

        <!-- Overlay -->
        <div v-if="showDetailPanel" class="api-key-panel-overlay" @click="closeDetailPanel"></div>

        <!-- Sliding Panel -->
        <div :class="['api-key-panel', { 'open': showDetailPanel }]">
            <div class="api-key-panel-header">
                <h2>{{ selectedItem.Id ? $t('administration.api_key.edit_api_key') : $t('administration.api_key.create_api_key') }}</h2>
                <button class="api-key-panel-close" @click="closeDetailPanel">
                    <i class="fas fa-times"></i>
                </button>
            </div>

            <div class="api-key-panel-body">

                <GlobSpinner :isLoading="isLoading"></GlobSpinner>

                <div class="api-key-form-group">
                    <label>{{ $t('administration.api_key.fields.api_name') }} *</label>
                    <InputText 
                        :fullWidth="true"
                        v-model="selectedItem.ApiName" 
                        :placeholder="$t('administration.api_key.placeholders.api_name')"
                    />
                </div>

                <div class="api-key-form-row">
                    <div class="api-key-form-group">
                        <label>{{ $t('administration.api_key.fields.valid') }}</label>
                        <InputSwitch
                            :value="selectedItem.Valid"
                            @update:modelValue="(val) => selectedItem.Valid = val"
                            :small="true"
                            :width="80"
                        />
                    </div>
                    <div class="api-key-form-group">
                        <label>{{ $t('administration.api_key.fields.expires') }}</label>
                        <InputText 
                            :fullWidth="true"
                            type="datetime-local"
                            v-model="selectedItem.Expires"
                            :placeholder="$t('administration.api_key.placeholders.expiration_date')"
                        />
                    </div>
                </div>

                <div class="api-key-form-group" v-if="selectedItem.Id">
                    <label>{{ $t('administration.api_key.fields.api_token') }}</label>
                    <div class="api-key-token-field">
                        <InputText 
                            :fullWidth="true"
                            :value="selectedItem.ApiToken" 
                            :disabled="true"
                            class="token-input"
                        />
                        <button class="api-key-copy-icon" @click="copyToken(selectedItem.ApiToken)">
                            <i class="fas fa-copy"></i>
                        </button>
                    </div>
                </div>

                <!-- API Scopes Section -->
                <div class="api-key-scopes-section">
                    <div class="api-key-scopes-header">
                        <h3>{{ $t('administration.api_key.endpoint_access') }}</h3>
                        <div class="api-key-scopes-presets">
                            <button class="api-key-scope-preset" @click="selectAllScopes(true)">
                                {{ $t('administration.api_key.select_all') }}
                            </button>
                            <button class="api-key-scope-preset" @click="selectAllScopes(false)">
                                {{ $t('administration.api_key.clear_all') }}
                            </button>
                        </div>
                    </div>

                    <table class="api-key-scopes-table">
                        <thead>
                            <tr>
                                <th>{{ $t('administration.api_key.table.endpoint') }}</th>
                                <th>{{ $t('administration.api_key.table.read') }}</th>
                                <th>{{ $t('administration.api_key.table.write') }}</th>
                            </tr>
                        </thead>
                        <tbody v-for="group in externalApiModules" :key="'group-' + group.GroupCode">
                            <tr class="api-key-scope-group-row">
                                <td colspan="3" class="api-key-scope-group-label">{{ group.GroupCode }}</td>
                            </tr>
                            <tr v-for="module in group.Modules" :key="'module-' + module.Id">
                                <td>
                                    <div class="api-key-scope-endpoint">{{ module.Endpoint }}</div>
                                    <div class="api-key-scope-desc">{{ module.Description }}</div>
                                </td>
                                <td class="api-key-scope-access">
                                    <GlobCheckbox 
                                        :value="getScopeState(module.Id).CanRead"
                                        @input="(e) => updateScope(module.Id, 'read', e)"
                                    />
                                </td>
                                <td class="api-key-scope-access">
                                    <GlobCheckbox 
                                        :value="getScopeState(module.Id).CanWrite"
                                        @input="(e) => updateScope(module.Id, 'write', e)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="api-key-panel-footer">
                <div>
                    <button class="button mr-2" @click="save">{{ $t('general.button.save') }}</button>
                    <button class="button button--orange" @click="closeDetailPanel">{{ $t('general.button.cancel') }}</button>
                </div>
                <button 
                    v-if="selectedItem.Id" 
                    class="api-key-panel-footer-right button button--red" 
                    @click="selectedIdToDelete = selectedItem.Id"
                >
                    {{ $t('general.button.delete') }}
                </button>
            </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <GlobConsequence 
            :show="selectedIdToDelete ? true : false" 
            @close="selectedIdToDelete = null"  
            @confirm="deleteItem()"
            :title="$t('administration.api_key.delete_api_key')" 
            :message="$t('administration.api_key.delete_confirm')"
        />
    </div>
</template>

<script>
import { ref, computed, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import apiKeyService from '@/services/api-key';
import InputText from '@/components/inputs/input-text.vue';
import InputSwitch from '@/components/inputs/input-switch.vue';
import GlobCheckbox from '@/components/globals/glob-checkbox.vue';
import GlobConsequence from '@/components/globals/glob-consequence.vue';
import { useNotifications } from '@/plugins/notifications';

export default {
    name: 'ApiKeyManagement',
    components: {
        InputText,
        InputSwitch,
        GlobCheckbox,
        GlobConsequence
    },
    setup() {
        const { tm } = useI18n();
        const notifications = useNotifications();
        const tableBody = ref([]);
        const selectedItem = ref({});
        const showDetailPanel = ref(false);
        const isLoading = ref(false);
        const selectedIdToDelete = ref(null);
        const searchString = ref('');
        const externalApiModules = ref([]);
        const selectedItemScopes = reactive({});

        const tableHead = computed(() => ([
            { Alias: 'ApiName', Value: tm('administration.api_key.fields.name') },
            { Alias: 'ApiToken', Value: tm('administration.api_key.fields.token') },
            { Alias: 'Status', Value: tm('administration.api_key.fields.status') },
            { Alias: 'ScopeCount', Value: tm('administration.api_key.fields.scopes') },
            { Alias: 'LastUsed', Value: tm('administration.api_key.fields.last_used') }
        ]));

        const filteredItems = computed(() => {
            if (!searchString.value) return tableBody.value;
            const search = searchString.value.toLowerCase();
            return tableBody.value.filter(item => 
                item.ApiName?.toLowerCase().includes(search) || 
                item.ApiToken?.toLowerCase().includes(search)
            );
        });

        const tableData = computed(() => ({
            head: tableHead.value,
            body: filteredItems.value
        }));

        const getStatusBadge = (item) => {
            if (item.Expires && new Date(item.Expires) < new Date()) {
                return `<span style="background: #f44336; color: white; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${tm('administration.api_key.status.expired')}</span>`;
            }
            return item.Valid
                ? `<span style="background: #4caf50; color: white; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${tm('administration.api_key.status.active')}</span>`
                : `<span style="background: #9e9e9e; color: white; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${tm('administration.api_key.status.inactive')}</span>`;
        };

        const renderFunctions = {
            ApiToken: (item) => {
                return item.ApiToken ? `<span style="font-family: monospace; background: #f5f5f5; padding: 2px 6px; border-radius: 4px; font-size: 0.75rem;">${item.ApiToken.substring(0, 20)}...</span>` : '';
            },
            Status: (item) => getStatusBadge(item),
            ScopeCount: (item) => {
                const count = item.Scopes?.length || 0;
                return count > 0 ? `<span style="background: #2196f3; color: white; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${count} ${tm('administration.api_key.endpoint_count_suffix')}</span>` : '-';
            },
            LastUsed: (item) => item.LastUsed || '-'
        };

        const actions = [
            {
                name: tm('general.button.edit'),
                color: 'blue',
                action: (item) => openDetailPanel(item)
            },
            {
                name: tm('administration.api_key.copy_token'),
                color: 'green',
                action: (item) => copyToken(item.ApiToken)
            },
            {
                name: tm('general.button.delete'),
                color: 'red',
                requirements: {
                    consequence: () => tm('administration.api_key.delete_confirm_short')
                },
                action: (item) => {
                    selectedIdToDelete.value = item.Id;
                    deleteItem();
                }
            }
        ];

        const loadItems = async () => {
            try {
                const response = await apiKeyService.getApiKeys();
                console.log('response', response)
                tableBody.value = response.data.data || [];
            } catch (error) {
                console.error('Failed to load API keys:', error);
                tableBody.value = [];
            }
        };

        const loadExternalApiModules = async () => {
            try {
                const response = await apiKeyService.getExternalApiModules();
                externalApiModules.value = response.data.data || [];
            } catch (error) {
                console.error('Failed to load API modules:', error);
                externalApiModules.value = [];
            }
        };

        const loadTokenScopes = async (tokenId) => {
            if (!tokenId) {
                Object.keys(selectedItemScopes).forEach(key => delete selectedItemScopes[key]);
                return;
            }
            try {
                const response = await apiKeyService.getApiKeyScopes(tokenId);
                const scopes = response?.data?.data || response?.data || [];

                const toBoolean = (value) => {
                    if (typeof value === 'boolean') return value;
                    if (typeof value === 'number') return value === 1;
                    if (typeof value === 'string') {
                        const normalized = value.trim().toLowerCase();
                        return normalized === '1' || normalized === 'true' || normalized === 'yes';
                    }
                    return false;
                };

                Object.keys(selectedItemScopes).forEach(key => delete selectedItemScopes[key]);

                scopes.forEach((scope) => {
                    const moduleId =
                        scope.ExternalApiModuleId ??
                        scope.externalApiModuleId ??
                        scope.external_api_module_id ??
                        scope.externalapimoduleid;

                    if (!moduleId) return;

                    const canRead =
                        scope.CanRead ??
                        scope.canRead ??
                        scope.can_read ??
                        scope.canread;

                    const canWrite =
                        scope.CanWrite ??
                        scope.canWrite ??
                        scope.can_write ??
                        scope.canwrite;

                    selectedItemScopes[moduleId] = {
                        CanRead: toBoolean(canRead),
                        CanWrite: toBoolean(canWrite)
                    };
                });
                console.log('scopes', scopes)
            } catch (error) {
                console.error('Failed to load scopes:', error);
            }
        };

        const getScopeState = (moduleId) => {
            if(selectedItemScopes[moduleId]){
                return selectedItemScopes[moduleId];
            }

            selectedItemScopes[moduleId] = {
                CanRead: false,
                CanWrite: false
            };

            return selectedItemScopes[moduleId];
        };

        const updateScope = (moduleId, type, inputValue) => {
            const currentScope = getScopeState(moduleId);

            let checked;
            if (typeof inputValue === 'boolean') {
                checked = inputValue;
            } else if (inputValue && typeof inputValue.val === 'boolean') {
                checked = inputValue.val;
            } else if (inputValue?.target && typeof inputValue.target.checked === 'boolean') {
                checked = inputValue.target.checked;
            } else {
                checked = !currentScope[type === 'read' ? 'CanRead' : 'CanWrite'];
            }

            if (type === 'read') {
                currentScope.CanRead = checked;
            } else if (type === 'write') {
                currentScope.CanWrite = checked;
            }
        };

        const selectAllScopes = (selectAll) => {
            externalApiModules.value.forEach(group => {
                group.Modules.forEach(module => {
                    if (!selectedItemScopes[module.Id]) {
                        selectedItemScopes[module.Id] = { CanRead: false, CanWrite: false };
                    }
                    selectedItemScopes[module.Id].CanRead = selectAll;
                    selectedItemScopes[module.Id].CanWrite = selectAll;
                });
            });
        };

        const openCreatePanel = () => {
            selectedItem.value = { Valid: true };
            Object.keys(selectedItemScopes).forEach(key => delete selectedItemScopes[key]);
            showDetailPanel.value = true;
        };

        const openDetailPanel = (item) => {
            const normalizedValid = typeof item.Valid === 'string'
                ? item.Valid === '1' || item.Valid.toLowerCase() === 'true'
                : !!item.Valid;

            selectedItem.value = { ...item, Valid: normalizedValid };
            loadTokenScopes(item.Id);
            showDetailPanel.value = true;
        };

        const closeDetailPanel = () => {
            showDetailPanel.value = false;
            selectedItem.value = {};
            Object.keys(selectedItemScopes).forEach(key => delete selectedItemScopes[key]);
        };

        const copyToken = (token) => {
            if (!token) return;
            navigator.clipboard.writeText(token);
            notifications.push(
                {
                    title: tm('general.success'),
                    msg: tm('administration.api_key.notifications.token_copied')
                },
                'success',
                false
            );
        };

        const save = async () => {
            isLoading.value = true;
            
            const scopesData = Object.entries(selectedItemScopes)
                .filter(([_, scope]) => scope.CanRead || scope.CanWrite)
                .map(([moduleId, scope]) => ({
                    ExternalApiModuleId: parseInt(moduleId),
                    CanRead: scope.CanRead,
                    CanWrite: scope.CanWrite
                }));
            
            const saveData = {
                ...selectedItem.value,
                Scopes: scopesData
            };

            try {
                if (selectedItem.value.Id) {
                    await apiKeyService.updateApiKey(selectedItem.value.Id, saveData);
                    notifications.push(
                        { title: tm('general.success'), msg: tm('administration.api_key.notifications.updated') },
                        'success',
                        false
                    );
                } else {
                    await apiKeyService.createApiKey(saveData);
                    notifications.push(
                        { title: tm('general.success'), msg: tm('administration.api_key.notifications.created') },
                        'success',
                        false
                    );
                }
                closeDetailPanel();
                loadItems();
            } catch (error) {
                console.error('Failed to save API key:', error);
                notifications.push(
                    { title: tm('general.error'), msg: tm('administration.api_key.notifications.save_failed') },
                    'error',
                    false
                );
            } finally {
                isLoading.value = false;
            }
        };

        const deleteItem = async () => {
            try {
                await apiKeyService.deleteApiKey(selectedIdToDelete.value);
                notifications.push(
                    { title: tm('general.success'), msg: tm('administration.api_key.notifications.deleted') },
                    'success',
                    false
                );
                selectedIdToDelete.value = null;
                closeDetailPanel();
                loadItems();
            } catch (error) {
                console.error('Failed to delete API key:', error);
                notifications.push(
                    { title: tm('general.error'), msg: tm('administration.api_key.notifications.delete_failed') },
                    'error',
                    false
                );
            }
        };

        const getStatusClass = (item) => {
            if (item.Expires && new Date(item.Expires) < new Date()) {
                return 'badge badge-danger';
            }
            return item.Valid ? 'badge badge-success' : 'badge badge-secondary';
        };

        const getStatusText = (item) => {
            if (item.Expires && new Date(item.Expires) < new Date()) {
                return tm('administration.api_key.status.expired');
            }
            return item.Valid ? tm('administration.api_key.status.active') : tm('administration.api_key.status.inactive');
        };

        onMounted(() => {
            loadItems();
            loadExternalApiModules();
        });

        return {
            tableData,
            actions,
            renderFunctions,
            selectedItem,
            showDetailPanel,
            isLoading,
            selectedIdToDelete,
            searchString,
            externalApiModules,
            selectedItemScopes,
            openCreatePanel,
            openDetailPanel,
            closeDetailPanel,
            copyToken,
            save,
            deleteItem,
            getScopeState,
            updateScope,
            selectAllScopes
        };
    }
};
</script>

<style lang="scss" scoped>
/* Panel overlay */
.api-key-panel-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.25);
    z-index: 100;
    opacity: 1;
    transition: opacity 0.25s;
}

/* Slide panel */
.api-key-panel {
    position: fixed;
    top: 0;
    right: 0;
    width: 600px;
    max-width: 95vw;
    height: 100vh;
    background: white;
    border-left: 1px solid #e2e7ed;
    z-index: 101;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);

    &.open {
        transform: translateX(0);
    }
}

.api-key-panel-header {
    padding: 20px 24px;
    border-bottom: 1px solid #e2e7ed;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f3f5f8;

    h2 {
        font-size: 18px;
        font-weight: 700;
        color: #1e2a36;
        margin: 0;
    }
}

.api-key-panel-close {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid #e2e7ed;
    border-radius: 4px;
    cursor: pointer;
    color: #5a6b7d;
    transition: all 0.15s;

    &:hover {
        background: white;
        border-color: #5a6b7d;
        color: #1e2a36;
    }
}

.api-key-panel-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
    position: relative;
}

.api-key-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 18px;
}

.api-key-token-field {
    position: relative;
    display: flex;
    align-items: center;

    .token-input {
        flex: 1;
    }
}

.api-key-copy-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    color: #8c9bab;
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 4px;
    transition: all 0.15s;

    &:hover {
        color: #3b82f6;
        background: #f7f9fb;
    }
}

.api-key-form-group {
    margin-bottom: 18px;

    label {
        display: block;
        font-size: .8rem;
        font-weight: 600;
        color: #5a6b7d;
        margin-bottom: 6px;
    }
}

/* Scopes section */
.api-key-scopes-section {
    margin-top: 24px;
}

.api-key-scopes-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    h3 {
        font-size: 14px;
        font-weight: 600;
        color: #1e2a36;
        margin: 0;
    }
}

.api-key-scopes-presets {
    display: flex;
    gap: 12px;
}

.api-key-scope-preset {
    font-size: 11.5px;
    color: $kits-blue;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    font-weight: 500;
    padding: 0;

    &:hover {
        text-decoration: underline;
    }
}

.api-key-scopes-table {
    width: 100%;
    border: 1px solid #e2e7ed;
    border-radius: 5px;
    overflow: hidden;
    border-collapse: separate;
    border-spacing: 0;
    font-size: .8rem;

    thead th {
        padding: 9px 12px;
        font-size: .7rem;
        font-weight: 600;
        text-transform: uppercase;
        color: white;
        background: $kits-steelGray;
        text-align: start;

        &:first-child {
            text-align: left;
        }
    }

    tbody td {
        padding: 10px 12px;
        font-size: .8rem;
        border-bottom: 1px solid #eaeff4;
        text-align: center;
        cursor: default;

        &:first-child {
            text-align: left;
        }
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    tbody tr:hover {
        background: #f7f9fb;
    }

    .api-key-scope-access{
        text-align: justify !important;
    }
}

.api-key-scope-endpoint {
    color: #1e2a36;
    font-weight: 500;
}

.api-key-scope-desc {
    font-size: .7rem;
    color: #8c9bab;
    margin-top: 1px;
}

/* Scope group styling */
.api-key-scope-group-row {
    &:hover {
        background: transparent !important;
    }
}

.api-key-scope-group-label {
    font-size: .7rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #8c9bab;
    background: #f7f9fb;
    padding: 8px 12px !important;
}


/* Panel footer */
.api-key-panel-footer {
    padding: 16px 24px;
    border-top: 1px solid #e2e7ed;
    display: flex;
    gap: 10px;
    align-items: center;
    background: #f3f5f8;
}

.api-key-panel-footer-right {
    display: flex;
    gap: 10px;
    margin-left: auto;
}

@media (max-width: 900px) {
    .api-key-panel {
        width: 100vw;
    }

    .api-key-form-row {
        grid-template-columns: 1fr;
    }
}
</style>
